import {
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Space } from "antd";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { appStore } from "src/appStore";
import {
  loadClassFilterMetadata,
  QueryBuilderFiltersManager,
  QueryBuilderManagerFacade,
} from "src/business/queryBuilder";
import {
  classQueryBuilderTemplatesLoader,
  createClassQueryBuilderTemplates,
  deleteClassQueryBuilderTemplate,
} from "src/business/queryBuilder/api/filterTemplatesApi";
import { onError } from "src/common/onError";
import { BlankLink } from "src/components/BlankLink";
import { ClosableTagsList } from "src/components/ClosableTag";
import { LeftCollapsedPanels } from "src/components/LeftCollapsedPanels";
import { LoaderBox } from "src/components/LoaderBox";
import { MdmTable } from "src/components/MdmTable";
import { PageLayout } from "src/components/PageLayout";
import { PageSubheader } from "src/components/PageSubheader";
import { QuerySearch } from "src/components/QuerySearch/QuerySearch";
import { RtColumnsSettingsButton } from "src/components/ReactiveTable/components/RtColumnsSettings";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import { ClassesTree } from "../../../business/classes/ClassesTree";
import * as styles from "./ClassesPage.module.less";
import { classesPageStore as pageStore, ClsPageData } from "./classesPageStore";

export const ClassesPage: React.FC = observer(() => {
  const { id = "" } = useParams();
  React.useEffect(() => {
    pageStore.init(id).catch(onError);
    return () => pageStore.dispose();
  }, [id]);
  return (
    <PageLayout title={pageStore.crumbs}>
      <LoaderBox
        remoteData={pageStore.data}
        drawReady={(data) => <Inner data={data} />}
      />
    </PageLayout>
  );
});

type PropsInner = {
  data: ClsPageData;
};

const Inner: React.FC<PropsInner> = observer(({ data }) => {
  const { tableStore, treeStore, classifier } = data;
  const queryBuilderManager = React.useMemo(
    () =>
      new QueryBuilderFiltersManager({
        metadataLoader: () => loadClassFilterMetadata(classifier.id),
        setFilter: (filter) =>
          tableStore.changeSingleFilter("queryBuilderFilter", filter),
        templatesLoader: classQueryBuilderTemplatesLoader(classifier.id),
        deleteTemplate: deleteClassQueryBuilderTemplate(classifier.id),
        createTemplate: createClassQueryBuilderTemplates(classifier.id),
      }),
    [classifier.id, tableStore],
  );

  React.useEffect(() => {
    queryBuilderManager.init().catch(onError);
    return () => {
      queryBuilderManager.dispose();
    };
  }, [queryBuilderManager]);

  const canCreate = classifier.canCreate !== false;
  const onChangeNode = (parentId: string | undefined) => {
    tableStore.changeSingleFilter("parentId", parentId);
    const node = treeStore.findRootNodeById(parentId ?? "");
    treeStore.setActiveNodeKey(node?.key ?? null);
  };
  const newClassQuery: Record<string, string> = {};
  const { activeNodeKey } = treeStore;
  const newClassId = activeNodeKey
    ? treeStore.findRootNodeByKey(activeNodeKey)?.id
    : undefined;
  if (newClassId) newClassQuery.classId = newClassId;
  const newClassUrl = makeUrl(
    PageUrl.newClass,
    { id: classifier.id },
    newClassQuery,
  );

  const filterTags = [];

  if (treeStore.activeNode) {
    filterTags.push({
      key: "tree",
      label: treeStore.activeNode.name,
      onClose: () => onChangeNode(undefined),
    });
  }

  if (queryBuilderManager.hasAppliedFilters) {
    filterTags.push(...queryBuilderManager.filterTags);
  }

  return (
    <LeftCollapsedPanels
      leftTitle={
        <Space>
          <FilterOutlined className={styles.icon} />
          <span>{t("Filter")}</span>
        </Space>
      }
      leftPart={
        <ClassesTree treeStore={treeStore} onChangeNode={onChangeNode} />
      }
      leftClosed
    >
      <div className={styles.box}>
        <PageSubheader title={pageStore.pageTitle} />
        <div className={styles.toolsTop}>
          <QuerySearch
            initialValue={tableStore.filters?.query ?? ""}
            onChange={(value) => tableStore.changeSingleFilter("query", value)}
          />
          <RtColumnsSettingsButton store={tableStore} />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => tableStore.reload()}
            title={t("Reload")}
          />
          {canCreate && (
            <Link to={newClassUrl}>
              <Button type="primary" icon={<PlusOutlined />}>
                {t("Class")}
              </Button>
            </Link>
          )}
        </div>
        <div className={styles.tools}>
          <ClosableTagsList
            tags={filterTags}
            action={
              <Button
                size="small"
                type="text"
                icon={<PlusOutlined />}
                onClick={() => queryBuilderManager.openNewDraft()}
              />
            }
          />
          {appStore.isMetadataEditor && (
            <BlankLink
              className={styles.settingsLink}
              href={makeUrl(PageUrl.managementClassifierCard, {
                id: classifier.id,
              })}
            >
              <Space>
                <SettingOutlined />
                Перейти в настройки
              </Space>
            </BlankLink>
          )}
          <Button
            type="link"
            icon={<FilterOutlined />}
            onClick={() => {
              queryBuilderManager.setTemplatesModalOpen(true);
            }}
          >
            Шаблоны
          </Button>
        </div>
        <div className={styles.tableBox}>
          <MdmTable
            store={tableStore}
            columns={pageStore.columns}
            resizableColumns
            rowClick={{
              type: "link",
              builder: (row) => makeUrl(PageUrl.class, { id: row.id }),
            }}
          />
        </div>
        <QueryBuilderManagerFacade manager={queryBuilderManager} />
      </div>
    </LeftCollapsedPanels>
  );
});
